import { isAnyOf, type Middleware } from '@reduxjs/toolkit'
import { applyRemovalResult, setCutoutCache, setItemMask, type BatchState } from './slice'

/** The reducers that take mask/cutout URLs in — and may decline to store them. */
const storesMaskOrCutout = isAnyOf(applyRemovalResult, setItemMask, setCutoutCache)

/**
 * Revokes the batch's `blob:` URLs once the store stops holding them.
 *
 * The cutout worker hands back its mask and cutout as object URLs, and those pin
 * their blobs in the document until revoked. The slice's eviction points —
 * `pruneUnreferencedData`, `clearBatch`, a cutout-cache overwrite — only delete the
 * string, which frees nothing. Reducers must stay pure, so the revocation lives
 * here: after each action, any blob URL the batch held before (or was handed in the
 * action) that no mask or cutout entry holds now is released.
 *
 * The diff is over the whole state, not per key, so a URL still referenced from
 * another image or version survives. Only `dataMap` and `cutoutCache` are scanned:
 * history snapshots carry version pointers, never URLs, and pruning already keeps
 * every version a snapshot can restore.
 */
export const batchObjectUrlRevocationMiddleware: Middleware<{}, { batch: BatchState }> =
  (api) => (next) => (action) => {
    const before = api.getState().batch
    const result = next(action)
    const after = api.getState().batch

    const handed = collectPayloadBlobUrls(action)
    const changed = before.dataMap !== after.dataMap || before.cutoutCache !== after.cutoutCache
    if (!changed && !handed.length) return result

    const kept = collectBlobUrls(after)
    const candidates = changed ? collectBlobUrls(before) : new Set<string>()
    // A URL the reducer declined to store (its image was removed mid-flight) has
    // no other owner — it goes too.
    for (const url of handed) candidates.add(url)
    for (const url of candidates) {
      if (!kept.has(url)) URL.revokeObjectURL(url)
    }
    return result
  }

function collectBlobUrls(state: BatchState): Set<string> {
  const urls = new Set<string>()
  for (const data of Object.values(state.dataMap)) {
    for (const mask of Object.values(data.masks ?? {})) {
      if (isBlobUrl(mask)) urls.add(mask)
    }
  }
  for (const versions of Object.values(state.cutoutCache)) {
    for (const entry of Object.values(versions)) {
      if (isBlobUrl(entry.url)) urls.add(entry.url)
    }
  }
  return urls
}

/** Blob URLs handed to a mask/cutout reducer. Other actions' payloads are never
 *  scanned: a `blob:` URL there belongs to someone else. */
function collectPayloadBlobUrls(action: unknown): string[] {
  if (!storesMaskOrCutout(action)) return []
  const payload = action.payload as { maskDataUrl?: string; cutoutDataUrl?: string; url?: string }
  return [payload.maskDataUrl, payload.cutoutDataUrl, payload.url].filter(isBlobUrl)
}

function isBlobUrl(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('blob:')
}
